// ============================================================
// Alle Zeichenketten der Anwendung an EINER Stelle — wortwörtlich aus
// SPEC §5.9 (Wortlaut verbindlich). Die Texte DES BLATTS stehen nicht hier,
// sondern im Kern (`TB`, SPEC §3.14): sie sind Teil des Druckerzeugnisses
// und müssen im Export identisch sein.
//
// Warum eine Datei und kein i18n-Apparat: es gibt genau eine Sprache
// (T17, Deutsch). Eine Bibliothek dafür wäre Gerüst ohne Gebäude. Der
// Gewinn dieser Datei ist ein anderer: jeder Satz, den ein Mensch liest,
// ist an einer Stelle nachlesbar und änderbar — und ein Refuter kann ihn
// gegen die SPEC diffen.
//
// `as const` ist Absicht: die Werte sind Literaltypen, ein Tippfehler in
// `TX.liste.titel` fällt beim Übersetzen auf und nicht im Bild.
// ============================================================
import { dauerText } from "./format";

export const TX = {
  marke: { name: "LILASp", produkt: "Zeitplaner" },
  // Nachgetragen gegenüber §5.9: das Wartezeichen der Schale (App.tsx,
  // §5.8.1) braucht einen Namen für die Vorlesehilfe, und §9.6.3 lässt
  // deutsche Zeichenketten in einer Komponente nur als `aria-label` AUS TX zu.
  laedt: "Wird geladen",
  kopf: {
    titelAria: "Titel des Zeitplans",
    titelPlatzhalter: "Grobzeitplan – Projektname",
    zurueck: "Rückgängig (Strg+Z)", vor: "Wiederholen (Strg+Umschalt+Z)",
    staende: "Stände", oeffnen: "Öffnen", speichern: "Speichern",
    exportieren: "Exportieren",
    themaHell: "Helles Thema", themaDunkel: "Dunkles Thema",
    navPlaene: "Pläne", navVorlagen: "Vorlagen", navPapierkorb: "Papierkorb",
    navVerwaltung: "Verwaltung", abmelden: "Abmelden",
    /** Name der Reiterleiste selbst (nachgetragen, s. `laedt`). */
    navAria: "Hauptbereiche",
  },
  buehne: {
    hinweiseMaus: ["Ziehen im leeren Bereich = neuer Balken", "Doppelklick = Text", "Entf löschen", "Strg+D duplizieren"],
    hinweiseTouch: ["Langes Drücken = neuer Balken", "Tippen = auswählen", "Zwei Finger = zoomen"],
    kleiner: "Kleiner", groesser: "Größer", aufBreite: "Auf Breite",
    /** §5.6.4 nennt den aria-Namen des dritten Knopfes, §5.9 kennt ihn nicht:
     *  der KNOPFTEXT ist „Auf Breite", die Vorlesehilfe hört den ganzen Satz. */
    aufBreiteAria: "Auf Breite einpassen",
    ausserhalb: (n: number) => n + (n === 1 ? " Eintrag außerhalb" : " Einträge außerhalb"),
    ausserhalbHilfe: "Diese Einträge liegen vor dem Beginn oder nach dem Ende des Zeitraums und werden nicht gezeichnet.",
    fremdeZeichen: (n: number, zeichen: string) =>
      n + " Zeichen (" + zeichen + ") kennt die Blattschrift nicht — im Export können sie anders umbrechen.",
  },
  links: {
    zeitraum: "Zeitraum", von: "Von", bis: "Bis",
    verdichten: "Ab hier zu Quartalen zusammenfassen", keine: "— keine —",
    quartalsbreite: "Breite der Quartalsspalte",
    ferienKurz: "Ferien", ferien: "Schulferien", ferienZeigen: "Ferienleiste anzeigen",
    bundesland: "Bundesland", ohne: "— ohne —", ferienNamen: "Ferien beschriften",
    ferienMin: "Kurze Ferien ausblenden (unter … Tagen)",
    tage: (n: number) => n + " Tage",
    ferienQuelle: (q: string, stand: string, bis: string) =>
      "Quelle: " + q + ", Stand " + stand + ", Daten bis " + bis + ". Bewegliche Ferientage sind enthalten.",
    ferienFehlen: (jahr: number) => "Für " + jahr + " liegen noch keine Ferien vor.",
    spuren: "Spuren", spurNameAria: "Name der Spur",
    zeileMinus: "Zeile entfernen", zeilePlus: "Zeile hinzufügen",
    zeilen: (n: number) => n + (n === 1 ? " Zeile" : " Zeilen"),
    hoch: "nach oben", runter: "nach unten", spurLoeschen: "Spur löschen",
    spurLoeschenFrage: (name: string, n: number) => "Spur „" + name + "“ mit " + n + " Einträgen löschen?",
    spurHinzu: "+ Spur hinzufügen",
    darstellung: "Darstellung", breite: "Blattbreite", zeilenhoehe: "Zeilenhöhe",
    schriftgroesse: "Schriftgröße",
    blatt: "Blatt", blattformat: "Blattformat",
    formatFrei: "Frei", formatA4: "A4 quer", formatA3: "A3 quer",
    formatHinweis:
      "Bei A4 und A3 steht das Blatt fest, der Inhalt muss hineinpassen \u2014 die Schrift behält im Druck ihr Maß. "
      + "\u201EFrei\u201C lässt das Blatt mit dem Zeitraum wachsen; die Seite entsteht dann erst beim Export.",
    blattMasse: (b: number, h: number) => "Festes Blatt, " + b + " \u00D7 " + h + " mm.",
    rand: "Rand",
    mm: (n: number) => (Math.round(n * 10) / 10).toString().replace(".", ",") + " mm",
    zeilenVerteilen: "Zeilen auf die Blatthöhe verteilen",
    zeilenhoeheErrechnet: "Zeilenhöhe (errechnet)",
    // Zwei Saetze, weil der eine Rat im anderen Zustand ins Leere geht: bei
    // eingeschaltetem Verteilen ist der Zeilenhoehen-Regler gesperrt.
    ueberlaufFest:
      "Der Inhalt ist höher als das Blatt, auch bei der kleinsten Zeilenhöhe. "
      + "Nimm Zeilen heraus, mach den Rand kleiner oder wähle A3 quer.",
    ueberlaufFrei:
      "Der Inhalt ist höher als das Blatt. Verringere die Zeilenhöhe, nimm Zeilen heraus, "
      + "mach den Rand kleiner oder wähle A3 quer.",
    zuFlach: (hoehe: string) =>
      "Die Zeilen sind nur noch " + hoehe + " hoch \u2014 für den Druck zu flach. "
      + "Wähle A3 quer, leg Zeilen zusammen oder verkleinere den Rand.",
    px: (n: number) => n + " px", prozent: (n: number) => Math.round(n * 100) + " %",
    mal: (n: number) => n.toFixed(2).replace(".", ",") + "×",
    raster: "Raster beim Ziehen",
    rasterWerte: [["1", "ganzer Monat"], ["0.5", "halber Monat"], ["0.25", "Viertelmonat"], ["0.0333", "frei (Tage)"]],
    hilfslinien: "Hilfslinien",
    hilfslinienExport: "Hilfslinien auch im Export",
    hilfslinienExportHinweis:
      "Die Linien gehen dann genau so in PDF, PNG und SVG, wie sie hier zu sehen sind.",
    vorlage: "Vorlage", vorlageFrage: (name: string) => "Vorlage „" + name + "“ laden? Der aktuelle Plan wird ersetzt.",
    vorlageSpeichern: "Als Vorlage speichern", vorlageName: "Name der Vorlage",
  },
  inspektor: {
    leer: "Kein Eintrag ausgewählt.",
    leerHinweisMaus: "Im leeren Bereich einer Spur waagerecht ziehen, um einen Balken anzulegen. Balken anklicken, um ihn hier zu bearbeiten.",
    leerHinweisTouch: "Lange auf eine leere Zeile drücken, um einen Balken anzulegen. Balken antippen, um ihn hier zu bearbeiten.",
    kopf: "Eintrag", duplizieren: "Duplizieren (Strg+D)", loeschen: "Löschen (Entf)",
    fertig: "Fertig",
    text: "Text", ausrichtung: "Ausrichtung",
    ausrichtungWerte: [["center", "zentriert"], ["left", "links"], ["right", "rechts"]],
    groesse: "Größe",
    groesseWerte: [["0", "automatisch"], ["10", "10 pt"], ["11", "11 pt"], ["12", "12 pt"], ["13", "13 pt"], ["15", "15 pt"]],
    stil: "Stil", farbe: "Eigene Farbe (überschreibt die Füllung)", farbeZurueck: "zurück",
    piktogramm: "Piktogramm", piktogrammOhne: "ohne", piktogrammLage: "Position",
    piktogrammLageWerte: [["center", "mittig"], ["left", "links"], ["right", "rechts"]],
    lage: "Lage", spur: "Spur", zeile: "Zeile", zeileN: (i: number) => "Zeile " + (i + 1),
    // Halbe Zeilen (T42): „Zeile 2, unten" = untere Hälfte der zweiten Zeile.
    zeileHalb: (r: number) => Number.isInteger(r) ? "Zeile " + (r + 1) : "Zeile " + (Math.floor(r) + 1) + ", unten",
    hoehe: "Höhe",
    hoeheN: (h: number) => h === 0.5 ? "½ Zeile" : h === 1 ? "1 Zeile"
      : (Number.isInteger(h) ? String(h) : Math.floor(h) + "½") + " Zeilen",
    senkrecht: "Text senkrecht",
    piktogrammFarbe: "Farbe des Piktogramms",
    bibliothek: "Aus der Bürobibliothek",
    bibliothekLeer: "Noch keine eigenen Symbole. Lade eine SVG-Datei hoch — sie steht danach im ganzen Büro zur Wahl.",
    hochladen: "SVG hochladen",
    hochladenLaeuft: "Wird gelesen …",
    hochgeladen: (name: string) => "„" + name + "“ steht jetzt in der Bibliothek.",
    symbolLoeschen: (name: string) => "„" + name + "“ aus der Bibliothek nehmen",
    symbolLoeschenFrage: (name: string) => "„" + name + "“ aus der Bibliothek nehmen? Pläne, die es schon verwenden, behalten es.",
    symbolGeloescht: "Aus der Bibliothek genommen.",
    frei: "Frei platzieren",
    freiHinweis: "Ziehen verschiebt den Eintrag jetzt pixelgenau, ohne Raster — auch aus seiner Zeile heraus. Zeitraum und Zeile bleiben, wie sie sind.",
    insRaster: "Zurück ins Raster",
    senkrechtHinweis: "Von unten nach oben lesbar. Stellt sich beim Ziehen an der Unterkante von selbst ein, sobald der Kasten höher als breit ist.",
    beginn: "Beginn", ende: "Ende",
    monatsanfang: "Monatsanfang", monatsmitte: "Monatsmitte", monatsende: "Monatsende",
    dauer: (n: number) => "Dauer: " + dauerText(n),
  },
  liste: {
    titel: "Meine Pläne", neu: "Neuer Plan", importieren: "PDF importieren",
    suche: "Pläne durchsuchen", alle: "Alle", meine: "Meine",
    sortiert: [["geaendert", "Zuletzt geändert"], ["titel", "Titel"], ["kunde", "Kunde"], ["angelegt", "Zuletzt angelegt"]],
    anzahl: (n: number) => n + (n === 1 ? " Plan" : " Pläne"),
    eintraege: (n: number) => n + (n === 1 ? " Eintrag" : " Einträge"),
    ausVorlage: "Aus Vorlage", papierkorb: "Papierkorb",
    feldTitel: "Titel", feldKunde: "Kunde/Stadt", feldNr: "Projekt-Nr. (optional)", feldSchlagworte: "Schlagworte",
    umbenennen: "Umbenennen", duplizieren: "Duplizieren", inPapierkorb: "In den Papierkorb",
    leer: "Noch kein Plan angelegt.",
    leerHinweis: "Mit „Neuer Plan“ aus einer Vorlage starten oder ein altes Grobzeitplan-PDF importieren.",
    // --- Nachgetragen gegenüber §5.9, mit Grund (§10 „Abweichungen") --------
    // §5.9 listet keine Spaltenköpfe, WEIL §5.7.2 die Liste als Karten
    // beschreibt. Die Tafel `.design/PlanlisteDesktop.dc.html` zeichnet auf
    // dem Schirm ab 860 px eine TABELLE, und §0.3 erklärt jeden dort
    // sichtbaren Text für verbindlich. Diese sieben Wörter stehen deshalb
    // hier — wortgleich mit der Tafel, damit nicht zwei Wahrheiten
    // entstehen. Die Karten (Telefon) benutzen sie nicht.
    spalten: {
      titel: "Titel", kunde: "Kunde / Stadt", nr: "Projekt-Nr.", zeitraum: "Zeitraum",
      schlagworte: "Schlagworte", geaendert: "Geändert", staende: "Stand",
    },
    // Der Name des Zeilenmenüs (`IconBtn dots-three`, §5.7.2). Ein IconBtn
    // OHNE `title` ist für eine Vorlesehilfe ein namenloser Knopf.
    mehr: "Weitere Aktionen",
    // Jedes `Modal` dieses Bildschirms braucht einen Weg heraus, der nicht
    // das Kreuz ist (§5.8.3: unter 640 px liegen die Fußknöpfe volle Breite
    // untereinander). §5.9 kennt das Wort nicht, weil die SPEC die Modale
    // nur nennt, nicht ausschreibt.
    abbrechen: "Abbrechen",
    /** Name der Auswahlgruppe „Sortierung" (das Feld selbst zeigt nur den Wert). */
    sortierung: "Sortierung",
  },
  anmelden: {
    titel: "Anmelden",
    unterzeile: "Mit deiner Büro-Adresse. Du bekommst einen sechsstelligen Code per E-Mail.",
    email: "E-Mail", emailPlatzhalter: "vorname@lilasp.de",
    domains: (d: string[]) => "Nur " + d.map((x) => "@" + x).join(" und "),
    bleiben: "Angemeldet bleiben (30 Tage)",
    codeSchicken: "Code anfordern",
    codeTitel: "Code eingeben",
    codeUnterzeile: "Sechs Ziffern aus der E-Mail, zehn Minuten haltbar.",
    ziffer: (i: number, n: number) => "Ziffer " + i + " von " + n,
    andereAdresse: "Andere Adresse", erneut: "Code erneut senden",
    erneutIn: (s: string) => "Erneut in " + s,
    pruefungFehler: "Die Prüfung von Cloudflare ließ sich nicht laden.",
    codeFehler: "Der Code stimmt nicht.",
    startFehler: "Der Code ließ sich nicht anfordern.",
  },
  staende: {
    titel: "Stände", festhalten: "Stand festhalten", notiz: "Notiz (optional)",
    /** §5.6.8 verlangt wörtlich einen `Chip tone="accent"` mit diesem Wort. */
    festgehalten: "Festgehalten",
    zeile: (v: number) => "Stand " + v,
    ansehen: "Ansehen", zuruecksetzen: "Zurücksetzen",
    zuruecksetzenFrage: (v: number) => "Auf Stand " + v + " zurücksetzen? Der jetzige Stand wird vorher festgehalten.",
    leer: "Noch kein Stand festgehalten.",
    hinweis: "Stände entstehen bei der ersten Speicherung, danach höchstens alle 15 Minuten — oder wenn du hier einen festhältst.",
  },
  sicherung: {
    gespeichert: (uhr: string, v: number) => "Gespeichert " + uhr + " · Stand " + v,
    ungesichert: "Nicht gespeichert",
    speichert: "Wird gespeichert …",
    fehler: "Nicht gespeichert — wird erneut versucht",
    entwurfZurueck: "Nicht gesicherte Änderungen von diesem Gerät wiederhergestellt.",
    entwurfVerworfen: "Ein älterer Entwurf auf diesem Gerät wurde verworfen.",
    entwurfFehlt: "Entwurf konnte nicht abgelegt werden — bitte den Plan sichern.",
    sitzungWeg: "Deine Anmeldung ist abgelaufen. Der Stand liegt auf diesem Gerät — melde dich neu an, dann ist er wieder da.",
  },
  konflikt: {
    satz: (name: string | null, v: number) =>
      (name ?? "Jemand") + " hat gerade gespeichert (Version " + v + "). Neu laden oder als Kopie sichern.",
    laden: "Serverstand laden", kopie: "Als Kopie speichern", schliessen: "Hinweis schließen",
  },
  wache: { frage: "Es liegen ungesicherte Änderungen vor. Trotzdem wechseln?" },
  import: {
    titel: "Import prüfen",
    laeuft: "Wird gelesen …",
    stufen: { text: "Text wird gelesen", heuristik: "Balken werden erkannt", sehmodell: "Sehmodell prüft nach" },
    seite: (n: number, ges: number) => "Seite " + n + " von " + ges,
    // §6.7.2 verlangt „bei mehreren Seiten die Blätterknöpfe", nennt aber
    // keinen Wortlaut. Zwei Wörter, hier und nicht in der Komponente.
    zurueck: "Zurück", weiter: "Weiter",
    hinweisBild: "Zwei Finger = zoomen · gestrichelt = erkannt",
    erkannt: (n: number) => "Erkannte Einträge (" + n + ")",
    gewaehlt: (n: number) => n + " gewählt",
    abNeunzig: "alles ab 0,9 übernehmen",
    uebernehmen: (n: number) => "Übernehmen (" + n + ")",
    verwerfen: "Verwerfen", andereDatei: "Andere Datei wählen",
    sicher: (p: number) => p + " % sicher", pruefen: (p: number) => p + " % prüfen",
    unsicher: (p: number) => p + " % unsicher",
    pflicht: "Rot umrandete Felder bitte prüfen.",
    umgehaengt: (n: number) => n + " Einträge ohne erkennbare Spur wurden in die erste Spur gelegt.",
    fertig: (name: string) => "Plan aus " + name + " angelegt.",
    zuLange: "Der Import antwortet nicht mehr. Bitte später noch einmal versuchen.",
    zuGross: "Die Datei ist zu groß (höchstens 25 MB für PDF, 5 MB für Excel).",
    falscherTyp: "Dieses Dateiformat nehmen wir nicht an. PDF, XLSX oder CSV.",
  },
  // Die Eintragsliste des Editors (§5.6.11). Eigene Gruppe, weil
  // `TX.liste.eintraege` schon die ZÄHLFORM der Planliste ist.
  eintraege: {
    suche: "Einträge durchsuchen",
    neu: "+ Eintrag",
  },
  // Die vier Reiter der Telefonleiste (§5.6.13).
  mobil: {
    blatt: "Blatt", eintraege: "Einträge", einstellungen: "Einstellungen", mehr: "Mehr",
  },
  export: {
    titel: "Exportieren",
    kacheln: { pdf: "Vektor, druckfertig", png: "3× Auflösung", svg: "Vektor, frei skalierbar",
               json: "Plan-Datei", xlsx: "Meilensteinliste", pptx: "Folie, bearbeitbar",
               mspdi: "XML, beide Richtungen", ics: "Meilensteine als Termine" },
    kachelNamen: { pdf: "PDF", png: "PNG", svg: "SVG", json: "JSON", xlsx: "Excel",
                   pptx: "PowerPoint", mspdi: "MS Project", ics: "Kalender" },
    seitenformat: "Seitenformat",
    blattIstSeite: (label: string) =>
      "Das Blatt ist schon " + label + ". Hier noch einmal einzupassen würde es um 7,65 % "
      + "verkleinern und den Rand verdoppeln — deshalb bleibt es bei der Blattgröße.",
    kopfzeileFest:
      "Die Kopfzeile kommt über das Blatt und macht die Ausgabe höher als die Seite. "
      + "Bei festem Blattformat gibt es sie deshalb nicht.", a3: "A3 quer", a4: "A4 quer", frei: "Blattbreite",
    // Das Feld „Auflösung" und seine drei Stufen stehen in §5.6.10 (nur bei PNG
    // sichtbar, Vorbelegung 288), aber nicht in der Liste von §5.9.
    aufloesung: "Auflösung",
    dpi96: "Bildschirm (96 dpi)", dpi288: "Standard (288 dpi)", dpi600: "Druck (600 dpi)",
    kopfzeile: "Kopfzeile mit Titel und Stand",
    hintergrund: "Hintergrund", papier: "Papier", transparent: "Transparent",
    erzeugen: (f: string) => f + " erzeugen", laeuft: "Wird erzeugt …",
    fehler: "Der Export ist fehlgeschlagen.",
    zuLange: "Der Export antwortet nicht mehr. Bitte später noch einmal versuchen.",
    stand: (v: number) => "Stand " + v,
  },
  papierkorb: {
    titel: "Papierkorb",
    hinweis: "Gelöschte Pläne bleiben 90 Tage hier und werden danach endgültig entfernt.",
    geloescht: (zeit: string, wer: string | null) => "Gelöscht " + zeit + (wer ? " von " + wer : ""),
    faellig: (datum: string) => "Wird endgültig entfernt am " + datum,
    wiederherstellen: "Wiederherstellen",
    leer: "Der Papierkorb ist leer.",
  },
  vorlagen: { titel: "Vorlagen", hinweis: "Vorlagen gelten für das ganze Büro.",
              verwenden: "Verwenden", loeschen: "Löschen",
              loeschenFrage: (name: string) => "Vorlage „" + name + "“ löschen?" },
  admin: { titel: "Verwaltung", email: "E-Mail", name: "Anzeigename", rolle: "Rolle",
           admin: "Admin", mitglied: "Mitglied", letzterLogin: "Letzte Anmeldung", plaene: "Pläne",
           entsperren: "Sperre aufheben",
           entsperrenHinweis: "Hebt die 24-Stunden-Sperre nach zu vielen Fehlversuchen auf.",
           // §5.7.7 verlangt wörtlich ein `Chip tone="warn"` mit „Gesperrt",
           // §5.9 hat das Wort nicht — nachgetragen, damit keine deutsche
           // Zeichenkette in einer Komponente steht (Fertigkriterium §9.6.3).
           gesperrt: "Gesperrt",
           /** Name der Auswahlgruppe je Zeile — der Wert allein sagt nicht, wozu. */
           rolleAria: (email: string) => "Rolle von " + email },
  fehler: {
    nichtGefunden: "Diese Seite gibt es nicht.",
    nichtGefundenHinweis: "Vielleicht wurde der Plan gelöscht.",
    zurListe: "Zur Planliste",
    blattKaputt: "Das Blatt konnte nicht gezeichnet werden.",
    nochmal: "Noch einmal versuchen", jsonSichern: "Letzte Fassung als JSON sichern",
    neuLaden: "Seite neu laden",
    // Der Standardsatz des Auffangkorbs (ScreenBoundary). Die Bühne des
    // Editors setzt stattdessen `blattKaputt` (§5.7.3) — beide Sätze stehen
    // damit hier und nicht in der Komponente (§9.6.3).
    umgefallen: "Hier ist etwas umgefallen.",
    umgefallenHinweis:
      "Der Bildschirm konnte nicht gezeichnet werden. Der Plan liegt auf dem Server und — solange etwas ungesichert war — auch auf diesem Gerät.",
    dateiKaputt: "Datei konnte nicht gelesen werden.",
    geladen: (name: string) => "Geladen: " + name,
    vorlageGeladen: "Vorlage geladen",
  },
  // T48 — Claude als Entwurfsautor
  claude: {
    nav: "Claude",
    titel: "Claude-Zugang",
    hinweis:
      "Mit diesem Zugang entwickelt Claude beim Schreiben von Angeboten den Grobzeitplan direkt hier als Entwurf. Claude kann Pläne und Vorlagen lesen, Entwürfe anlegen und nur die eigenen Entwürfe überarbeiten — nichts löschen, keinen übernommenen Plan ändern.",
    adresse: "Adresse des Connectors",
    kopieren: "Kopieren",
    kopiert: "Adresse kopiert",
    schritteApp: "In der Claude-App oder auf claude.ai",
    schritteAppText:
      "Einstellungen → Connectors → „Benutzerdefinierten Connector hinzufügen“, Name „Zeitplaner“, obige Adresse eintragen, dann „Verbinden“. Es öffnet sich diese Seite zur Freigabe.",
    schritteCode: "In Claude Code",
    befehl: "claude mcp add --transport http zeitplaner ",
    schritteCodeText: "Danach in Claude Code /mcp aufrufen und „Authenticate“ wählen.",
    verbindungen: "Verbundene Claude-Zugänge",
    keine: "Noch kein Claude verbunden.",
    trennen: "Trennen",
    getrennt: "Verbindung getrennt",
    seit: (wann: string) => "verbunden " + wann,
    zuletzt: (wann: string) => "zuletzt aktiv " + wann,
    nieBenutzt: "noch nicht benutzt",
    // Freigabeseite /verbinden/:id
    freigabeTitel: "Claude verbinden",
    freigabeFrage: (client: string, ziel: string) => `${client} (${ziel}) möchte mit deinem Konto auf den Zeitplaner zugreifen.`,
    darf: "Erlaubt wird:",
    darfNicht: "Nicht erlaubt: Pläne löschen, übernommene Pläne ändern, Exporte, Verwaltung.",
    erlauben: "Erlauben",
    ablehnen: "Ablehnen",
    weiter: "Zurück zu Claude …",
    abgelaufen: "Diese Freigabe gibt es nicht (mehr). Bitte in Claude neu verbinden.",
    // Entwürfe
    entwurf: "Entwurf · Claude",
    entwurfBanner: "Diesen Plan hat Claude als Entwurf angelegt. Solange er Entwurf ist, kann Claude ihn weiter überarbeiten.",
    uebernehmen: "Als fertigen Plan übernehmen",
    uebernommen: "Übernommen — Claude kann diesen Plan jetzt nur noch lesen.",
  },
} as const;
